import { forwardRef, useId } from 'react';
import { X } from 'lucide-react';
import { cn } from '../lib/cn';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  /** Мягкий лимит символов: показывает счётчик и подсвечивает превышение (не режет жёстко). */
  counterMax?: number;
  /** Текст ошибки под полем (напр. превышение длины). */
  error?: string | null;
  /** Кнопка очистки поля (×) в правом верхнем углу. */
  onClear?: () => void;
}

/** Многострочное поле с floating-label, счётчиком и валидацией длины (мягкий лимит). */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, label, counterMax, error, onClear, value, id, ...props }, ref) => {
    const autoId = useId();
    const fieldId = id ?? autoId;
    const len = typeof value === 'string' ? value.length : 0;
    const over = counterMax != null && len > counterMax;
    const invalid = over || (error != null && error !== '');
    const hasValue = typeof value === 'string' && value !== '';
    return (
      <div>
        <div
          className={cn(
            'relative flex flex-col rounded-[var(--radius-control)] border bg-surface px-3 py-2 transition-colors',
            invalid ? 'border-brand' : 'border-border-strong',
            className
          )}
        >
          {label && (
            <label
              htmlFor={fieldId}
              className={cn('text-[11px] font-medium', invalid ? 'text-brand' : 'text-fg-muted')}
            >
              {label}
            </label>
          )}
          {onClear && hasValue && (
            <button
              type="button"
              onClick={onClear}
              aria-label="Очистить"
              className="absolute right-2 top-2 grid h-6 w-6 place-items-center rounded-md text-fg-muted transition-colors hover:text-fg"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          <textarea
            ref={ref}
            id={fieldId}
            value={value}
            aria-invalid={invalid ? true : undefined}
            aria-describedby={invalid && error ? `${fieldId}-err` : undefined}
            rows={3}
            className={cn(
              // 16px на тач-устройствах — против автозума iOS Safari при фокусе (promt-86 §7).
              'w-full resize-none bg-transparent text-sm text-fg outline-none placeholder:text-fg-muted pointer-coarse:text-base',
              onClear && 'pr-7'
            )}
            {...props}
          />
          {counterMax != null && (
            <div
              className={cn(
                'mt-1 text-right text-[11px]',
                over ? 'font-medium text-danger' : 'text-fg-muted'
              )}
            >
              {len}/{counterMax}
            </div>
          )}
        </div>
        {invalid && error && (
          <p id={`${fieldId}-err`} className="mt-1 text-xs text-danger">
            {error}
          </p>
        )}
      </div>
    );
  }
);
Textarea.displayName = 'Textarea';
